import React, { useState, useEffect, useCallback } from "react";
import { Select, MenuItem, SelectChangeEvent } from "@mui/material";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/yapa-logo.png";
import { getYears, getStatuses, getBandsWithFilters, setStars, getStarBands } from "../ModalOverlay/ModalApi/ModalApi";
import "./BandList.css";
const statusLabels: Record<string, string> = {
  tak: "zaakceptowane",
  nie: "niezaakceptowane",
};
const BandList = () => {
  const [items, setItems] = useState<{ id: number; name: string }[]>([]);
  const [filteredItems, setFilteredItems] = useState<{ id: number; name: string }[]>([]);
  const [starredItems, setStarredItems] = useState<string[]>([]);
  const [years, setYears] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);

  const DEFAULT_YEAR = "2025";
  const DEFAULT_STATUS = "tak";

  const [selectedYear, setSelectedYear] = useState<string>(DEFAULT_YEAR);
  const [selectedStatus, setSelectedStatus] = useState<string>(DEFAULT_STATUS);
  
  const [searchQuery, setSearchQuery] = useState<string>("");

  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();

  const fetchStars = useCallback(async () => {
    try {
      await getStarBands(axiosPrivate, (starredBands: string[]) => {
        setStarredItems(starredBands);
      });
    } catch (error) {
      console.error("Błąd podczas pobierania gwiazd:", error);
      setStarredItems([]);
    }
  }, [axiosPrivate]);

  // Pobranie zespołów
  const fetchBands = useCallback(async () => {
    try {
      await getBandsWithFilters(axiosPrivate, selectedYear, selectedStatus, (items: string[]) => {
        const bands = items.map((name, index) => ({
          id: index, // Generate a consistent ID (e.g., index starting from 1)
          name,
        }));
        setItems(bands); // Save these bands in the state
      });
    } catch (error) {
      console.error("Błąd podczas pobierania zespołów:", error);
      setItems([]);
    }
  }, [axiosPrivate, selectedYear, selectedStatus]);

  useEffect(() => {
    fetchBands();
    fetchStars();
  }, [fetchBands, fetchStars]);

  // Pobranie lat i statusów do filtrowania
  useEffect(() => {
    getYears(axiosPrivate, setYears);
    getStatuses(axiosPrivate, setStatuses);
  }, [axiosPrivate]);

  
  
  // Filtrowanie i sortowanie zespołów (gwiazdy na górze)
  useEffect(() => {
    const filtered = items.filter((band) =>
      band.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Sortowanie: gwiazdy na górze
    const sortedBands = filtered.sort((a, b) => {
      const isAStar = starredItems.includes(a.name) ? 1 : 0;
      const isBStar = starredItems.includes(b.name) ? 1 : 0;
      return isBStar - isAStar; // Gwiazdy na górze
    });

    setFilteredItems(sortedBands);
  }, [items, searchQuery, starredItems]);

  const handleEdit = (index: number) => {
    sessionStorage.setItem('editedBandId', index.toString());
    
  };

  const handleStarClick = async (band: { id: number; name: string }) => {
    const isCurrentlyStarred = starredItems.includes(band.name);

    try {
        await setStars(
            axiosPrivate,
            band.name, // Send the band name instead of the band ID
            isCurrentlyStarred ? 0 : 1, // Toggle the star status
            () => {
                // Success: Update the starredItems state
                fetchStars();
            },
            (error) => {
                console.error("Błąd podczas zmiany statusu gwiazdy:", error);
            }
        );
    } catch (error) {
        console.error("Wystąpił problem:", error);
    }
};

  

  const handleYearChange = (event: SelectChangeEvent) => {
    setSelectedYear(event.target.value);
  };

  const handleStatusChange = (event: SelectChangeEvent) => {
    setSelectedStatus(event.target.value);
  };

  return (
    <div className="artists-container">
      <div>
        <img src={logo} alt="Yapa Logo" className="logo" />

        <div className="header-filters">
          <input
            className="modal-input"
            type="text"
            placeholder="Szukaj..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <h1>ZESPOŁY 🎶</h1>
          <div className="filters">
            <Select
              value={years.includes(selectedYear) ? selectedYear : DEFAULT_YEAR}
              onChange={handleYearChange}
              displayEmpty
              className="filter-select-bands"
              sx={{
                backgroundColor: "#222222",
                border: "1px solid #ff8800",
                color: "#ffffff",
                "& .MuiSelect-icon": { color: "#ff8800" },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
            <Select
              value={statuses.includes(selectedStatus) ? selectedStatus : ""}
              onChange={handleStatusChange}
              displayEmpty
              className="filter-select-bands"
              sx={{
                backgroundColor: "#222222",
                border: "1px solid #ff8800",
                color: "#ffffff",
                "& .MuiSelect-icon": { color: "#ff8800" },
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
              }}
            >
              {statuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {statusLabels[status] || status}
                </MenuItem>
              ))}
            </Select>
          </div>
        </div>

        <div className="table-actions-container">
          <div className="table-container">
            <table className="artists-table">
              <thead>
                <tr>
                  <th>Nazwa zespołu</th>
                  <th>
                    <span className="desktop-header">Edycja i gwiazdka</span>
                    <span className="mobile-header">Edycja</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((band, index) => (
                  <tr key={band.id} className={index % 2 === 0 ? "even-row" : "odd-row"}>
                    <td>{band.name}</td>
                    <td>
                      <button
                        className="btn icon-btn"
                        onClick={() => handleEdit(index)}
                      >
                        ✏️
                      </button>
                      <button
                        className="btn icon-btn"
                        onClick={() => handleStarClick(band)}
                      >
                        {starredItems.includes(band.name) ? "⭐" : "☆"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="actions">
            <button className="btn" onClick={() => navigate(-1)}>
              Wróć
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BandList;
